/**
 * IXVerify v2 — Audit log viewer (ixv2 WP-4.2; plan §4 screens inventory:
 * "Audit log viewer (admin, live DB)").
 *
 * Admin-only, READ-ONLY window onto `ixv_audit_log` — the append-only Part-11
 * trail (R4). v1's viewer read the frozen Supabase snapshot; this one reads
 * the live data plane through the gated `GET /api/ixverify/audit` route, with
 * SERVER-side filters (date range / actor / action / entity) and pagination.
 * CSV export is server-generated with the same filters — and the export is
 * itself an audited action.
 *
 * Dense laptop-first admin table (R7); rows are PHI-free by construction
 * (R3), so nothing here ever renders a DOB or document text.
 */
import { useCallback, useEffect, useState } from 'react';
import { ScrollText, FileDown, Inbox, ShieldAlert } from 'lucide-react';
import {
  PageHeader,
  RefreshButton,
  EmptyState,
  useToast,
  secondaryBtnCls,
  fieldCls,
} from '../../ui';
import { useAuth } from '../../contexts/AuthContext';
import {
  listAudit,
  exportAuditCsv,
  errorMessage,
  isAdminRequired,
  IxvAuditEntry,
  AuditFilters,
} from '../lib/api';

const PAGE_SIZE = 50;

/** Known entity (target_type) values — matches the backend's write sites. */
const TARGET_TYPES = [
  'ixv_session',
  'ixv_asset',
  'ixv_session_kit',
  'ixv_subject',
  'ixv_report',
  'site_study',
  'ixv_settings',
  'ixv_saved_recipient',
  'ixv_audit_log',
];

const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function shortId(value: string | null): string {
  return value ? `${value.slice(0, 8)}…` : '';
}

function detailsPreview(details: Record<string, unknown>): string {
  const json = JSON.stringify(details ?? {});
  return json === '{}' ? '' : json;
}

export default function AuditLog() {
  const { toast } = useToast();
  const { isAdmin, isSuperAdmin } = useAuth();
  const canView = isAdmin || isSuperAdmin;

  const [entries, setEntries] = useState<IxvAuditEntry[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(!canView);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Applied (server-side) filters. The actor input commits on Enter/blur so a
  // half-typed email never fires a query per keystroke.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [actor, setActor] = useState('');
  const [actorDraft, setActorDraft] = useState('');

  const filters: AuditFilters = { from, to, action, target_type: targetType, actor };

  const fetchEntries = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const res = await listAudit(
        { from, to, action, target_type: targetType, actor },
        { limit: PAGE_SIZE, offset },
      );
      setEntries(res.entries);
      setActions(res.actions);
      setHasMore(res.entries.length === PAGE_SIZE);
      setDenied(false);
    } catch (err) {
      if (isAdminRequired(err)) {
        setDenied(true);
      } else {
        toast(errorMessage(err, 'Failed to load the audit log'), { type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [canView, from, to, action, targetType, actor, offset, toast]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const applyActor = () => {
    if (actorDraft.trim() !== actor) {
      setActor(actorDraft.trim());
      setOffset(0);
    }
  };

  const changeFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setOffset(0);
  };

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const blob = await exportAuditCsv(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ixverify-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast('Audit CSV downloaded');
    } catch (err) {
      toast(errorMessage(err, 'Failed to export the audit log'), { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  if (denied) {
    return (
      <div className="space-y-4">
        <PageHeader icon={ScrollText} title="Audit log" />
        <EmptyState
          icon={ShieldAlert}
          title="Administrator access required"
          hint="The audit trail is visible to site administrators only. Contact your administrator if you need a record from it."
          variant="card"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={ScrollText}
        title="Audit log"
        subtitle="Append-only 21 CFR Part 11 trail — every action, actor, and client recorded"
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadCsv}
              disabled={exporting || loading}
              className={secondaryBtnCls}
            >
              <FileDown className="h-4 w-4" aria-hidden="true" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <RefreshButton onClick={fetchEntries} loading={loading} />
          </div>
        }
      />

      {/* Chromeless filter bar — all filters apply SERVER-side */}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label htmlFor="ixv-audit-from" className={labelCls}>From</label>
          <input
            id="ixv-audit-from"
            type="date"
            className={`${fieldCls} min-h-[44px] sm:min-h-0`}
            value={from}
            onChange={(e) => changeFilter(setFrom)(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="ixv-audit-to" className={labelCls}>To</label>
          <input
            id="ixv-audit-to"
            type="date"
            className={`${fieldCls} min-h-[44px] sm:min-h-0`}
            value={to}
            onChange={(e) => changeFilter(setTo)(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="ixv-audit-action" className={labelCls}>Action</label>
          <select
            id="ixv-audit-action"
            className={`${fieldCls} min-h-[44px] sm:min-h-0`}
            value={action}
            onChange={(e) => changeFilter(setAction)(e.target.value)}
          >
            <option value="">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ixv-audit-entity" className={labelCls}>Entity</label>
          <select
            id="ixv-audit-entity"
            className={`${fieldCls} min-h-[44px] sm:min-h-0`}
            value={targetType}
            onChange={(e) => changeFilter(setTargetType)(e.target.value)}
          >
            <option value="">All entities</option>
            {TARGET_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ixv-audit-actor" className={labelCls}>Actor (email or user id)</label>
          <input
            id="ixv-audit-actor"
            type="text"
            placeholder="user@yoursite.com"
            className={`${fieldCls} min-h-[44px] w-56 sm:min-h-0`}
            value={actorDraft}
            onChange={(e) => setActorDraft(e.target.value)}
            onBlur={applyActor}
            onKeyDown={(e) => { if (e.key === 'Enter') applyActor(); }}
          />
        </div>
      </div>

      {!loading && entries.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No audit entries found"
          hint="No entries match the current filters for this site."
          variant="card"
        />
      ) : (
        <>
          {/* Dense laptop-first table (R7) — wide content scrolls in its own container */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    {['Time', 'Action', 'Actor', 'Entity', 'Client', 'Details'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {entries.map((e) => (
                    <tr key={e.id} className="align-top transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-500 dark:text-gray-400">
                        {fmtDateTime(e.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                          {e.action}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {e.actor ? (
                          <>
                            <span className="block">{e.actor.name || e.actor.email || shortId(e.actor.id)}</span>
                            {e.actor.name && e.actor.email && (
                              <span className="block text-xs text-gray-500 dark:text-gray-400">{e.actor.email}</span>
                            )}
                          </>
                        ) : (
                          <span title={e.actor_id || undefined}>{e.actor_id ? shortId(e.actor_id) : '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {e.target_type ? (
                          <>
                            <span className="block">{e.target_type}</span>
                            {e.target_id && (
                              <span className="block text-xs tabular-nums text-gray-500 dark:text-gray-400" title={e.target_id}>
                                {shortId(e.target_id)}
                              </span>
                            )}
                          </>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        <span className="block tabular-nums">{e.ip_address || '—'}</span>
                        {e.user_agent && (
                          <span className="block max-w-[16rem] truncate text-xs text-gray-500 dark:text-gray-400" title={e.user_agent}>
                            {e.user_agent}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {detailsPreview(e.details) ? (
                          <code
                            className="block max-w-[24rem] truncate text-xs text-gray-600 dark:text-gray-300"
                            title={detailsPreview(e.details)}
                          >
                            {detailsPreview(e.details)}
                          </code>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pager */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              className={secondaryBtnCls}
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {entries.length > 0 ? `${offset + 1}–${offset + entries.length}` : '—'}
            </span>
            <button
              type="button"
              className={secondaryBtnCls}
              disabled={!hasMore || loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
